import { useState, type ChangeEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, XCircle, UserCheck, Shield, Clock, ExternalLink } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  Modal,
  PageHeader,
  Textarea,
  Input,
  useToast,
  type ColumnDef,
} from '@/components/ui'

// ── Types ────────────────────────────────────────────────────────────────────

interface PendingApproval {
  approvalId: string
  instanceStepId: string
  instanceId: string
  instanceTitle: string
  stepTitle: string | null
  stepDescription: string | null
  processStartedAt: string
  startedByName: string | null
  dueDate: string | null
  stageOrder: number
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '–'
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(iso))
}

function isOverdue(dueDate: string | null): boolean {
  if (!dueDate) return false
  return new Date(dueDate) < new Date()
}

// ── Decision Modal ────────────────────────────────────────────────────────────

interface DecisionModalProps {
  approval: PendingApproval | null
  mode: 'approve' | 'reject' | 'delegate' | null
  onClose: () => void
  onSuccess: () => void
}

function DecisionModal({ approval, mode, onClose, onSuccess }: DecisionModalProps) {
  const [comment, setComment] = useState('')
  const [delegateToUserId, setDelegateToUserId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const { toast } = useToast()

  const decideMut = useMutation({
    mutationFn: async () => {
      if (!approval || !mode) return
      if (mode === 'delegate') {
        await apiClient.post(`/prozesse/approvals/${approval.approvalId}/delegate`, {
          toUserId: delegateToUserId.trim(),
          comment: comment.trim() || undefined,
        })
      } else {
        await apiClient.post(`/prozesse/approvals/${approval.approvalId}/decide`, {
          decision: mode,
          comment: comment.trim() || undefined,
        })
      }
    },
    onSuccess: () => {
      toast({
        title:
          mode === 'approve' ? 'Freigabe erteilt' :
          mode === 'reject' ? 'Freigabe abgelehnt' :
          'Freigabe delegiert',
        tone: mode === 'approve' ? 'success' : mode === 'reject' ? 'warning' : 'info',
      })
      setComment('')
      setDelegateToUserId('')
      setError(null)
      onSuccess()
    },
    onError: (e: unknown) => {
      const axErr = e as { response?: { data?: { error?: string } }; message?: string }
      setError(axErr.response?.data?.error ?? axErr.message ?? 'Unbekannter Fehler')
    },
  })

  const isRejectDisabled = mode === 'reject' && !comment.trim()
  const isDelegateDisabled = mode === 'delegate' && !delegateToUserId.trim()

  if (!approval || !mode) return null

  return (
    <Modal
      open
      onOpenChange={(open) => { if (!open) onClose() }}
      title={
        mode === 'approve' ? 'Freigabe erteilen' :
        mode === 'reject' ? 'Freigabe ablehnen' :
        'Freigabe delegieren'
      }
    >
      <div className="space-y-4">
        <div className="text-body text-neutral-700">
          <span className="font-medium">{approval.instanceTitle}</span>
          {approval.stepTitle && (
            <span className="text-neutral-500"> — {approval.stepTitle}</span>
          )}
        </div>

        {mode === 'delegate' && (
          <Input
            label="Delegieren an (User-ID)"
            value={delegateToUserId}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setDelegateToUserId(e.target.value)}
            placeholder="User-ID des Vertreters"
            hint="Die User-ID des Nutzers, dem die Freigabe übertragen werden soll"
          />
        )}

        <Textarea
          label={
            mode === 'reject' ? 'Begründung (Pflicht)' :
            mode === 'delegate' ? 'Optionale Notiz' :
            'Optionaler Kommentar'
          }
          value={comment}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value)}
          placeholder={
            mode === 'reject' ? 'Begründung für die Ablehnung...' :
            mode === 'delegate' ? 'Optionaler Hinweis für den Vertreter...' :
            'Optionaler Kommentar...'
          }
          rows={3}
        />

        {error && (
          <ErrorState size="sm" title="Fehler" description={error} />
        )}

        <div className="flex items-center gap-2 justify-end pt-2">
          <Button variant="ghost" onClick={onClose} disabled={decideMut.isPending}>
            Abbrechen
          </Button>
          <Button
            variant={mode === 'approve' ? 'primary' : mode === 'reject' ? 'danger' : 'secondary'}
            loading={decideMut.isPending}
            disabled={isRejectDisabled || isDelegateDisabled}
            leadingIcon={
              mode === 'approve' ? <CheckCircle2 className="w-4 h-4" /> :
              mode === 'reject' ? <XCircle className="w-4 h-4" /> :
              <UserCheck className="w-4 h-4" />
            }
            onClick={() => decideMut.mutate()}
          >
            {mode === 'approve' ? 'Freigabe erteilen' :
             mode === 'reject' ? 'Ablehnen' :
             'Delegieren'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function ApprovalsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [selectedApproval, setSelectedApproval] = useState<PendingApproval | null>(null)
  const [modalMode, setModalMode] = useState<'approve' | 'reject' | 'delegate' | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['prozesse-approvals-pending'],
    queryFn: () =>
      apiClient.get<PendingApproval[]>('/prozesse/approvals/pending').then((r) => r.data),
    refetchInterval: 60_000,
  })

  function openModal(approval: PendingApproval, mode: 'approve' | 'reject' | 'delegate') {
    setSelectedApproval(approval)
    setModalMode(mode)
  }

  function closeModal() {
    setSelectedApproval(null)
    setModalMode(null)
  }

  function handleSuccess() {
    closeModal()
    void queryClient.invalidateQueries({ queryKey: ['prozesse-approvals-pending'] })
  }

  const columns: ColumnDef<PendingApproval>[] = [
    {
      id: 'process',
      header: 'Prozess',
      cell: ({ row }) => (
        <div>
          <div className="font-medium text-neutral-900">{row.original.instanceTitle}</div>
          {row.original.stepTitle && (
            <div className="text-xs text-neutral-500 mt-0.5">{row.original.stepTitle}</div>
          )}
        </div>
      ),
    },
    {
      id: 'startedBy',
      header: 'Gestartet von',
      cell: ({ row }) => (
        <span className="text-neutral-700">
          {row.original.startedByName ?? '–'}
        </span>
      ),
    },
    {
      id: 'dueDate',
      header: 'Fällig',
      cell: ({ row }) => {
        const overdue = isOverdue(row.original.dueDate)
        return (
          <span className={overdue ? 'text-red-600 font-medium' : 'text-neutral-700'}>
            {row.original.dueDate ? formatDate(row.original.dueDate) : '–'}
            {overdue && ' ⚠'}
          </span>
        )
      },
    },
    {
      id: 'startedAt',
      header: 'Gestartet',
      cell: ({ row }) => (
        <span className="text-neutral-500 text-sm">
          {formatDate(row.original.processStartedAt)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5 justify-end">
          <Button
            size="xs"
            variant="ghost"
            leadingIcon={<ExternalLink className="w-3.5 h-3.5" />}
            onClick={() => navigate(`/prozesse/${row.original.instanceId}`)}
            aria-label="Prozessdetails öffnen"
          >
            Details
          </Button>
          <Button
            size="xs"
            variant="primary"
            leadingIcon={<CheckCircle2 className="w-3.5 h-3.5" />}
            onClick={() => openModal(row.original, 'approve')}
          >
            Freigeben
          </Button>
          <Button
            size="xs"
            variant="danger"
            leadingIcon={<XCircle className="w-3.5 h-3.5" />}
            onClick={() => openModal(row.original, 'reject')}
          >
            Ablehnen
          </Button>
          <Button
            size="xs"
            variant="ghost"
            leadingIcon={<UserCheck className="w-3.5 h-3.5" />}
            onClick={() => openModal(row.original, 'delegate')}
          >
            Delegieren
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-brand-500" />
            Offene Freigaben
          </span>
        }
        description="Prozesse, die deine Freigabe benötigen"
      />

      {isLoading && <LoadingState label="Offene Freigaben werden geladen…" />}

      {error && (
        <ErrorState
          title="Fehler beim Laden"
          description="Offene Freigaben konnten nicht geladen werden."
          details={error instanceof Error ? error.message : String(error)}
        />
      )}

      {data && data.length === 0 && (
        <Card>
          <Card.Body>
            <EmptyState
              icon={<CheckCircle2 className="w-6 h-6 text-green-500" />}
              title="Keine offenen Freigaben"
              description="Du hast aktuell keine ausstehenden Freigabeanfragen."
            />
          </Card.Body>
        </Card>
      )}

      {data && data.length > 0 && (
        <>
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
            <Clock className="w-4 h-4 shrink-0" />
            <span>
              {data.length} offene Freigabe{data.length !== 1 ? 'n' : ''} warte{data.length === 1 ? 't' : 'n'} auf deine Entscheidung
            </span>
          </div>
          <DataTable
            columns={columns}
            data={data}
            getRowId={(row) => row.approvalId}
          />
        </>
      )}

      <DecisionModal
        approval={selectedApproval}
        mode={modalMode}
        onClose={closeModal}
        onSuccess={handleSuccess}
      />
    </div>
  )
}
